<template>
    <x-form ref="form">
        <x-container fluid grid-list-md>
            <x-layout>
                <x-flex xs12>
                    <h2>{{ editing ? 'Edit' : 'Add' }} facility</h2>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="facility.name"
                        label="Name"
                        placeholder=" "
                        :rules="[v => ruleRequired(v, 'Name')]"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-select
                        v-model="facility.status"
                        class="status-select"
                        :items="statuses"
                        item-text="label"
                        item-value="value"
                        label="Status"
                        :rules="[v => ruleRequired(v, 'Status')]"
                        append-icon="keyboard_arrow_down"
                        attach=".status-select"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="facility.brokerage"
                        label="Brokerage"
                        placeholder=" "
                        suffix="%"
                        :rules="[
                            v => ruleRequired(v, 'Brokerage'),
                            v => ruleGreaterThanOrEqualTo(v, 0, 'Brokerage'),
                            v => ruleLessThanOrEqualTo(v, 100, 'Brokerage')
                        ]"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="facility.UMR"
                        label="UMR"
                        placeholder=" "
                        :rules="[
                            v => ruleRequired(v, 'UMR'),
                            v => ruleUmr(v, '', 'UMR format not valid')
                        ]"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-autocomplete
                        v-model="facility.producing_broker_id"
                        class="producing-broker-select"
                        :items="producingBrokers"
                        label="Producing broker"
                        placeholder=" "
                        item-text="name"
                        item-value="id"
                        :rules="[v => v === null || ruleRequired(v, 'Producing broker')]"
                        append-icon="keyboard_arrow_down"
                        attach=".producing-broker-select"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-autocomplete
                        v-model="facility.client_id"
                        class="client-select"
                        :items="clients"
                        label="Client"
                        placeholder=" "
                        item-text="name"
                        item-value="id"
                        :rules="[v => ruleRequired(v, 'Client')]"
                        append-icon="keyboard_arrow_down"
                        attach=".client-select"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-checkbox
                        label="Do not present Statement of Facts to client"
                        v-model="facility.do_not_display_sof"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-autocomplete
                        v-model="facility.entity_ids"
                        class="entity-select"
                        :items="clientEntities"
                        label="Entity"
                        placeholder="No entity selected"
                        :rules="[v => ruleMinSelected(v, 1, false, 'At least 1 entity must be selected')]"
                        item-text="name"
                        item-value="id"
                        :search-input.sync="entitiesSearchInput"
                        append-icon="keyboard_arrow_down"
                        multiple
                        attach=".entity-select"
                    >
                        <template slot="selection" slot-scope="{ item, index }">
                            <template v-if="index === 0 && !entitiesSearchInput">
                                <span v-if="facility.entity_ids.length === 1">
                                    {{ item.name }}
                                </span>

                                <span v-else-if="facility.entity_ids.length > 1">
                                    {{ facility.entity_ids.length }} entities selected
                                </span>
                            </template>
                        </template>
                    </x-autocomplete>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-autocomplete
                        v-model="facility.insurer_ids"
                        class="insurer-select"
                        :items="insurers"
                        label="Insurers"
                        placeholder="No insurers selected"
                        item-text="name"
                        item-value="id"
                        :search-input.sync="insurersSearchInput"
                        append-icon="keyboard_arrow_down"
                        multiple
                        attach=".insurer-select"
                    >
                        <template slot="selection" slot-scope="{ item, index }">
                            <template v-if="index === 0 && !insurersSearchInput">
                                <span v-if="facility.insurer_ids.length === 1">
                                    {{ item.name }}
                                </span>

                                <span v-else-if="facility.insurer_ids.length > 1">
                                    {{ facility.insurer_ids.length }} insurers selected
                                </span>
                            </template>
                        </template>
                    </x-autocomplete>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-radio-group
                        v-model="isCaptiveFacility"
                        :row="true"
                        :rules="[
                            v => ruleRequired(v, 'Captive insurer')
                        ]"
                    >
                        <template v-slot:label>Captive facility?</template>

                        <x-radio label="Yes" :value="true" />

                        <x-radio label="No" :value="false" />
                    </x-radio-group>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-autocomplete
                        v-if="isCaptiveFacility"
                        v-model="facility.captive_insurer_id"
                        class="captive-insurer-select"
                        :items="captiveInsurers"
                        label="Captive insurer"
                        placeholder=" "
                        item-text="name"
                        item-value="id"
                        :rules="[v => ruleRequired(v, 'Captive insurer')]"
                        append-icon="keyboard_arrow_down"
                        attach=".captive-insurer-select"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-autocomplete
                        v-if="insurerSecurities.length > 0"
                        v-model="facility.security_ids"
                        class="security-select"
                        :items="insurerSecurities"
                        label="Securities"
                        placeholder="No securities selected"
                        item-text="name"
                        item-value="id"
                        :search-input.sync="securitiesSearchInput"
                        append-icon="keyboard_arrow_down"
                        multiple
                        attach=".security-select"
                        no-data="No insurers selected"
                    >
                        <template slot="selection" slot-scope="{ item, index }">
                            <template v-if="index === 0 && !securitiesSearchInput">
                                <span v-if="facility.security_ids.length === 1">{{ item.name }}</span>

                                <span v-else>{{ facility.security_ids.length }} securities selected</span>
                            </template>
                        </template>

                        <template slot="item" slot-scope="{ item, tile }">
                            <x-list-tile-action>
                                <x-checkbox
                                    :input-value="tile.props.value"
                                />
                            </x-list-tile-action>

                            <x-list-tile-content>
                                <x-list-tile-title>{{ item.insurer_company_name }}</x-list-tile-title>

                                <x-list-tile-sub-title>{{ item.name }}</x-list-tile-sub-title>
                            </x-list-tile-content>
                        </template>
                    </x-autocomplete>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-autocomplete
                        v-model="facility.product_ids"
                        class="product-select"
                        :items="products"
                        label="Products"
                        placeholder="No products selected"
                        item-text="name"
                        item-value="id"
                        :rules="[v => ruleMinSelected(v, 1, false, 'At least 1 product must be selected')]"
                        :search-input.sync="productsSearchInput"
                        append-icon="keyboard_arrow_down"
                        multiple
                        attach=".product-select"
                    >
                        <template slot="selection" slot-scope="{ item, index }">
                            <template v-if="index === 0 && !productsSearchInput">
                                <span v-if="facility.product_ids.length === 1">{{ item.name }}</span>

                                <span v-else-if="facility.product_ids.length > 1">{{ facility.product_ids.length }} products selected</span>
                            </template>
                        </template>
                    </x-autocomplete>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-select
                        label="Waiting period interest"
                        placeholder="Waiting period interest"
                        v-model="facility.waiting_period_interest"
                        class="waiting-period-interest-select"
                        :items="waitingPeriodInterestOptions"
                        item-value="value"
                        item-text="name"
                        :rules="[
                            v => ruleRequired(v, 'Waiting period interest'),
                        ]"
                        append-icon="keyboard_arrow_down"
                        attach=".waiting-period-interest-select"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-select
                        v-model="facility.waiting_period_values"
                        class="waiting-period-select"
                        :items="waitingPeriods"
                        label="Available waiting periods"
                        placeholder="Available waiting periods"
                        item-value="value"
                        item-text="name"
                        :rules="[
                            v => ruleMinSelected(v, 1, false, 'At least 1 waiting period must be selected')
                        ]"
                        append-icon="keyboard_arrow_down"
                        multiple
                        attach=".waiting-period-select"
                    >
                        <template slot="selection" slot-scope="{ item, index }">
                            <template v-if="index === 0">
                                <span v-if="facility.waiting_period_values.length === 1">{{ item.name }}</span>

                                <span v-else-if="facility.waiting_period_values.length > 1">{{ facility.waiting_period_values.length }} options selected</span>
                            </template>
                        </template>
                    </x-select>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-autocomplete
                        v-model="facility.premium_payment_term"
                        :items="premiumPaymentTermOptions"
                        label="Premium payment term"
                        placeholder=" "
                        item-text="name"
                        item-value="value"
                        class="premium-payment-term-select"
                        :rules="[v => ruleRequired(v, 'Premium payment term')]"
                        append-icon="keyboard_arrow_down"
                        attach=".premium-payment-term-select"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-menu
                        v-model="inceptionDateMenu"
                        :close-on-content-click="false"
                        lazy
                        transition="scale-transition"
                        max-width="300"
                        full-width
                        offset-y
                        nudge-bottom="22"
                        attach=".inception-date-input"
                    >
                        <template v-slot:activator="{ on }">
                            <x-text-field
                                v-model="facility.inception_date"
                                class="inception-date-input"
                                label="Inception date"
                                placeholder=" "
                                :rules="[
                                  v => ruleRequired(v, 'Inception date')
                                ]"
                                append-icon="event"
                                readonly
                                v-on="on"
                            />
                        </template>

                        <x-date-picker
                            v-model="facility.inception_date"
                            @input="inceptionDateMenu = false"
                        />
                    </x-menu>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-menu
                        v-model="endDateMenu"
                        :close-on-content-click="false"
                        lazy
                        transition="scale-transition"
                        max-width="300"
                        full-width
                        offset-y
                        nudge-bottom="22"
                        attach=".end-date-input"
                    >
                        <template v-slot:activator="{ on }">
                            <x-text-field
                                v-model="facility.end_date"
                                class="end-date-input"
                                label="End date"
                                placeholder=" "
                                :rules="[v => ruleRequired(v, 'End date')]"
                                append-icon="event"
                                readonly
                                v-on="on"
                            />
                        </template>

                        <x-date-picker
                            v-model="facility.end_date"
                            @input="endDateMenu = false"
                            :min="facility.inception_date"
                        />
                    </x-menu>
                </x-flex>
            </x-layout>

            <x-layout v-if="editing">
                <x-flex xs12>
                    <document-list
                        upload
                        :documentable-id="facility.id"
                        documentable-type="facility"
                        attach-menu
                    />
                </x-flex>
            </x-layout>

            <x-layout mt-4>
                <x-flex>
                    <x-btn
                        color="tertiary"
                        @click="$emit('close')"
                    >
                        Cancel
                    </x-btn>
                </x-flex>

                <x-flex text-xs-right>
                    <x-btn color="primary" @click="$emit('submit')">
                        Submit

                        <i class="material-icons ml-2">trending_flat</i>
                    </x-btn>
                </x-flex>
            </x-layout>
        </x-container>
    </x-form>
</template>

<script>
  import CheckIcon from '~/components/Shared/UIComponents/CheckIcon';
  import DocumentList from '~/components/Shared/UIComponents/DocumentList.vue';
  import rules from '~/mixins/rules';
  import WaitingPeriodInterestOptions from '~/data/wpi_options.json';

  export default {
    name: 'FacilityForm',
    mixins: [rules],
    components: {
      CheckIcon,
      DocumentList
    },
    props: {
      facility: {
        type: Object,
        required: true
      },
      producingBrokers: {
        type: Array,
        default: () => []
      },
      clients: {
        type: Array,
        default: () => []
      },
      insurers: {
        type: Array,
        default: () => []
      },
      captiveInsurers: {
          type: Array,
          default: () => []
      },
      products: {
          type: Array,
          default: () => []
      },
      securities: {
          type: Array,
          default: () => []
      },
      editing: {
        type: Boolean,
        default: false
      },
      waitingPeriods: {
        type: Array,
        default: () => []
      },
    },
    data () {
      return {
        insurersSearchInput: false,
        productsSearchInput: false,
        securitiesSearchInput: false,
        entitiesSearchInput: false,
        inceptionDateMenu: false,
        endDateMenu: false,
        isCaptiveFacility: false,
        waitingPeriodInterestOptions: WaitingPeriodInterestOptions,
        premiumPaymentTermOptions: [
            {
                'name': '30 Days',
                'value': 30
            },
            {
                'name': '45 Days',
                'value': 45
            },
            {
                'name': '60 Days',
                'value': 60
            }
        ],
        statuses: [
            {
                value: 0,
                label: "Inactive"
            },
            {
                value: 1,
                label: "Active"
            },
        ],
      }
    },
    methods: {
      validate () {
        return this.$refs.form.validate();
      },
      resetValidation () {
        this.$refs.form.resetValidation();
      }
    },
    mounted() {
        this.isCaptiveFacility = this.facility.captive_insurer_id >= 1;
    },
    watch: {
        isCaptiveFacility(newValue) {
            if (newValue === false) {
                this.facility.captive_insurer_id = null;
            }
        },
        insurerSecurities(newSecurities) {
            const newSecurityIds = newSecurities.map(security => security.id);
            this.facility.security_ids = this.facility.security_ids.filter(id => newSecurityIds.includes(id));
        },
    },
      computed: {
          insurerSecurities() {
              if (!this.securities) {
                  return [];
              }

              return this.securities.filter(security => this.facility.insurer_ids.includes(security.insurer_company_id)) || [];
          },
          clientEntities() {
                const self = this;
                let clientEntities = [];
                if (self.facility.client_id) {
                    this.clients.find((client) => client.id === self.facility.client_id)
                        ?.entities.forEach((entity) => {
                            if (entity.status) {
                                clientEntities.push(entity);
                            }
                        });
                }
                this.facility.entity_ids = clientEntities.filter(c => self.facility.entity_ids.includes(c.id)).map(e => e.id);

                return clientEntities;
            },
      }
  };
</script>

<style lang="scss" scoped>
.x-menu {
    position: relative;
}
.x-list {
    .x-list__tile {
        height: auto;
        .x-list__tile__sub-title, .x-list__tile__title {
            white-space: normal;
        }
    }
}

.inception-date-input,
.end-date-input {
    position: relative;
}
</style>
